/* eslint-disable jsx-a11y/no-autofocus */
import { useDisclosure } from '@chakra-ui/hooks';
import {
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    useToast
} from '@chakra-ui/react';
import { EventFieldsFragment, EventScheduleFieldsFragment } from '@graphql/hasura';
import { useEventAccess, useLiveTed } from '@lib/firebase';
import dynamic from 'next/dynamic';
import React, { useEffect } from 'react';
import { FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const TedResults = dynamic(() => import('./TedResults'));
const TedForm = dynamic(() => import('./TedForm'));

type TedLiveDrawerProps = {
    event: EventFieldsFragment;
    fullMode?: Boolean;
};

const TedLiveDrawer: FunctionComponent<TedLiveDrawerProps> = ({ event, fullMode = false }) => {
    const { canViewSchedule } = useEventAccess(event);
    const { ted, mode, currentUserAnswers, loading, submitUserAnswer } = useLiveTed(event.id);
    const { isOpen, onClose, onOpen } = useDisclosure();
    const toast = useToast();
    const intl = useIntl();

    async function openTedDrawer() {
        try {
            await document.exitFullscreen();
        } catch (_) {
            //do nothing
        }
        onOpen();
    }

    function controlLiveTed() {
        const canView =
            ted &&
            canViewSchedule(
                event.event_schedules.find(
                    (es) => es.id == ted?.event_schedule_id
                ) as EventScheduleFieldsFragment,
                ['exhibition', 'livestream', 'ondemand']
            );
        if (canView && mode === 'questions' && !currentUserAnswers) {
            openTedDrawer();
        } else if (canView && mode === 'results') {
            openTedDrawer();
        } else {
            onClose();
        }
    }

    useEffect(controlLiveTed, [ted, mode, currentUserAnswers]);

    return (
        <Drawer
            size={fullMode ? 'full' : 'md'}
            isOpen={isOpen}
            placement={fullMode ? 'top' : 'right'}
            onClose={onClose}
            closeOnOverlayClick={false}
            autoFocus={false}>
            <DrawerContent>
                {fullMode ? null : (
                    <DrawerCloseButton
                        variant="outline"
                        pos="absolute"
                        right="4"
                        top="4"
                        color="white"
                    />
                )}

                <DrawerHeader backgroundColor="highlight" color="highlightText" mb="4">
                    {mode == 'results' ? (
                        <FormattedMessage
                            id="ted.header.results.title"
                            defaultMessage="Ergebnisse"
                        />
                    ) : (
                        <FormattedMessage id="ted.header.asking.title" defaultMessage="Fragen" />
                    )}
                </DrawerHeader>
                <DrawerBody>
                    {ted && !loading && mode == 'questions' ? (
                        <TedForm
                            ted={ted}
                            initialValues={currentUserAnswers ? currentUserAnswers.values : {}}
                            onSubmit={async (values) => {
                                await submitUserAnswer(values);
                                toast({
                                    title: intl.formatMessage({
                                        id: 'ted.answer.submitted',
                                        defaultMessage: 'Antwort gesendet'
                                    }),
                                    status: 'success',
                                    duration: 7000
                                });
                                onClose();
                            }}
                        />
                    ) : null}
                    {ted && !loading && mode == 'results' ? (
                        <TedResults ted={ted} eventId={event.id} />
                    ) : null}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
};
export default TedLiveDrawer;
